"use client";

import { getStrapiMedia } from "@/utils/api-helpers";
import Image from "next/image";
import Arrow from "@/app/assets/arrow.svg";
import { useRef } from "react";
import gsap from "gsap";
import Link from "next/link";

export default function ProjectCard({
    data,
    index,
    fluid = false,
}: {
    data: any;
    index?: number;
    fluid?: boolean;
}) {
    const cardRef = useRef<HTMLDivElement>(null);
    const imageWrapperRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const bottomContentRef = useRef<HTMLDivElement>(null);

    const handleMouseEnter = () => {
        const overlayHeight = overlayRef.current?.offsetHeight ?? 0;
        const cardHeight = cardRef.current?.offsetHeight ?? 0;
        const scale =
            cardHeight > 0 ? (cardHeight - overlayHeight) / cardHeight : 0.5;

        gsap.to(imageWrapperRef.current, {
            scale,
            duration: 0.5,
            ease: "power2.out",
        });
        gsap.to(overlayRef.current, {
            opacity: 1,
            duration: 0.5,
            ease: "power2.out",
        });
        gsap.to(bottomContentRef.current, {
            opacity: 0,
            duration: 0.3,
            ease: "power2.out",
        });
    };

    const handleMouseLeave = () => {
        gsap.to(imageWrapperRef.current, {
            scale: 1,
            duration: 0.5,
            ease: "power2.out",
        });
        gsap.to(overlayRef.current, {
            opacity: 0,
            duration: 0.5,
            ease: "power2.out",
        });
        gsap.to(bottomContentRef.current, {
            opacity: 1,
            duration: 0.3,
            delay: 0.2,
            ease: "power2.out",
        });
    };

    return (
        <>
            {/* Desktop layout — hover to reveal info */}
            <div
                ref={cardRef}
                className={`relative lg:rounded-3xl md:rounded-2xl rounded-xl overflow-hidden aspect-[3/1.9] hidden lg:block ${
                    fluid ? "w-full" : "w-[52vw] xl:w-[48vw] 2xl:w-[40vw]"
                }`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Link href={data.project.button.link}>
                    <div
                        ref={overlayRef}
                        className="absolute bottom-0 left-0 right-0 z-0 px-2 pb-4 flex justify-between opacity-0 overflow-visible"
                    >
                        <div className="mt-3 w-[80%]">
                            <h2 className="font-bold text-[#263244] mt-auto mb-auto text-lg 2xl:text-2xl w-[90%]">
                                {data.project.heading}
                            </h2>
                            <p className="text-sm xl:text-base color-[#263244] mt-3">
                                {data.project.description}
                            </p>
                        </div>
                        <div className="bg-purple rounded-full w-12 h-12 flex justify-center items-center mt-3">
                            <Image
                                src={Arrow}
                                width={20}
                                height={20}
                                alt="rotating img"
                                className="!h-[20px] !w-[20px] invert brightness-0 filter"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    <div
                        ref={imageWrapperRef}
                        className="relative w-full h-full z-[1] lg:rounded-3xl md:rounded-2xl rounded-xl overflow-hidden"
                        style={{ transformOrigin: "top left" }}
                    >
                        <div className="absolute z-10 w-full h-full flex-col flex justify-between px-10">
                            <div className="mt-5 backdrop-blur-md bg-white/70 rounded-lg px-3 py-1 w-fit border border-white/50">
                                <p className="text-purple font-semibold leading-none m-0">
                                    {data.project.serviceName}
                                </p>
                            </div>
                            <div
                                ref={bottomContentRef}
                                className="w-full flex justify-between items-center mb-5"
                            >
                                <h1 className="text-white font-bold text-h3-fontsize w-[70%] bottom-0">
                                    {data.project.heading}
                                </h1>
                                <div className="bg-purple rounded-full w-14 h-14 flex justify-center items-center">
                                    <Image
                                        src={Arrow}
                                        width={22}
                                        height={22}
                                        alt="rotating img"
                                        className="!h-[22px] !w-[22px] invert brightness-0 filter"
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                        </div>

                        <Image
                            src={getStrapiMedia(data.project.image.url)}
                            alt={data.project.image.alternativeText || ""}
                            fill
                            className="w-full absolute object-cover lg:rounded-3xl md:rounded-2xl rounded-xl aspect-3/2 z-[1]"
                            sizes="50vw"
                            loading="lazy"
                        />
                        <div className="absolute bottom-0 left-0 right-0 h-1/3 z-[2] bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
                    </div>
                </Link>
            </div>

            {/* Mobile layout — info always visible, image natural aspect ratio */}
            <div
                className={`block lg:hidden rounded-xl overflow-hidden ${
                    fluid ? "w-full" : "md:w-[50vw] w-[70vw]"
                }`}
            >
                <Link href={data.project.button.link}>
                    <div className="relative w-full">
                        <Image
                            src={getStrapiMedia(data.project.image.url)}
                            alt={data.project.image.alternativeText || ""}
                            width={800}
                            height={600}
                            className="w-full h-auto rounded-xl"
                            sizes="85vw"
                            loading="lazy"
                        />
                        <div className="absolute top-3 left-4 backdrop-blur-md bg-white/50 rounded-lg px-3 py-1 w-fit border border-white/50 z-10">
                            <p className="text-purple font-semibold leading-none m-0 text-sm">
                                {data.project.serviceName}
                            </p>
                        </div>
                        <div className="absolute bottom-3 right-3 bg-purple rounded-full w-10 h-10 flex justify-center items-center z-10">
                            <Image
                                src={Arrow}
                                width={16}
                                height={16}
                                alt="arrow"
                                className="!h-[16px] !w-[16px] invert brightness-0 filter"
                            />
                        </div>
                    </div>
                    <div className="py-3">
                        <h2 className="font-bold text-lg text-darkGrey mb-1">
                            {data.project.heading}
                        </h2>
                        <p className="text-sm text-darkGrey2 leading-relaxed">
                            {data.project.description}
                        </p>
                    </div>
                </Link>
            </div>
        </>
    );
}
